import { useCallback } from 'react';

import { CallToActionObjectType } from 'groq/objects/CallToActionObject';
import { Maybe } from 'utils/types';
import { LocaleString } from '__generated__/graphql-sanity';
import { LinkObjectType } from 'groq/objects/LinkObject';
import { isAbsoluteUrl } from 'utils/urls';

import { useSiteConfig } from './useSiteConfig';

const dereferenceUrlObj = (
  localeKey: string,
  language: string,
  obj?: Maybe<LocaleString & { en?: Maybe<string> }>
): string | undefined => {
  return (
    obj?.[localeKey] || obj?.[language] || obj?.en_GB || obj?.en_US || obj?.en
  );
};

const _getLink = (
  parent: Maybe<CallToActionObjectType | LinkObjectType> | undefined,
  localeKey: string,
  language: string
) =>
  !!parent
    ? dereferenceUrlObj(localeKey, language, parent?.category) ||
      parent?.article ||
      parent?.marketing ||
      parent?.support ||
      parent?.product ||
      parent?.core ||
      parent?.absolute ||
      ''
    : '';

export const useLinkResolver = () => {
  const { locale, language, localizeUrlPath } = useSiteConfig();

  const getLink = useCallback(
    (obj?: Maybe<CallToActionObjectType | LinkObjectType>) => {
      const localeKey = locale.replace('-', '_');

      return obj
        ? ('linkConfig' in obj &&
            _getLink(obj?.linkConfig, localeKey, language)) ||
            _getLink(obj, localeKey, language)
        : '';
    },
    [language, locale]
  );

  const getLocalizedLink = useCallback(
    (
      obj?: Maybe<
        | CallToActionObjectType
        | LinkObjectType
        | { href?: Maybe<string>; query?: string }
      >,
      localizedAbsolute?: boolean
    ) => {
      const link = obj && 'href' in obj ? obj.href : getLink(obj);

      if (!link) {
        return '/';
      }

      if (isAbsoluteUrl(link)) return link;
      return `${localizeUrlPath(link, localizedAbsolute)}${obj?.query || ''}`;
    },
    [getLink, localizeUrlPath]
  );

  return { getLink, getLocalizedLink };
};
